import React, { useEffect, useState } from "react"
import { injectIntl } from "react-intl"
import * as Yup from "yup"
import { makeStyles } from "@material-ui/core/styles"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import { Box, Card, Grid, Typography, Link } from "@material-ui/core"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import {
  FormikForm,
  FormikFormContainer,
  TextInputForm,
  PhoneNumberInput,
  Checkbox,
} from "@ridecell/web-components-material"
import { Field } from "formik"
import { Ridecell } from "@ridecell/web-sdk"
import { parsePhoneNumberFromString as parsePhoneNumber } from "libphonenumber-js"

import ConsentOptions from "./ConsentOptions"
import useGlobalContext from "../../../utils/customHooks/useGlobalContext"
import LoadingButton from "../../LoadingButton"
import useCurrentLocation from "../../../utils/customHooks/useCurrentLocation"
import messages from "./messages"
import policyKeyMessages from "./PolicyKeyMessages"
import useCustomer from "../../../utils/customHooks/useCustomer"

const nameRegex = /^[\w-,']*$/

const validationSchema = (
  formatMessage,
  passwordPolicy,
  businessInviteCodeMandatory
) =>
  Yup.object().shape({
    first_name: Yup.string()
      .required(formatMessage(messages.nameIsRequired))
      .matches(nameRegex, formatMessage(messages.firstNameNoSpecialChars))
      .nullable(),
    last_name: Yup.string()
      .required(formatMessage(messages.lastNameIsRequired))
      .matches(nameRegex, formatMessage(messages.lastNameNoSpecialChars))
      .nullable(),
    email: Yup.string()
      .required(formatMessage(messages.emailIsRequired))
      .email()
      .nullable(),
    password: Yup.string()
      .required(formatMessage(messages.passwordIsRequired))
      .min(
        passwordPolicy.minimumNumberOfCharacters,
        formatMessage(messages.mustBeAtMiniumCharacters, {
          minChars: passwordPolicy.minimumNumberOfCharacters,
        })
      )
      .nullable(),
    phone_number: Yup.string()
      .required(formatMessage(messages.phoneNumberIsRequired))
      .test(
        "validPhone",
        formatMessage(messages.invalidPhoneNumber),
        (value) => {
          const phoneNumber = value && parsePhoneNumber(value)
          return phoneNumber && phoneNumber.isValid()
        }
      )
      .nullable(),
    terms_accepted: Yup.bool().oneOf([true]),
    business_profile_activation_code:
      businessInviteCodeMandatory &&
      Yup.string()
        .required(formatMessage(messages.businessInviteCodeRequired))
        .nullable(),
  })

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  card: {
    margin: "24px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
  },
  checkbox: {
    padding: "0 !impotant",
  },
})

const CreateCustomer = ({
  handleNext,
  intl: { formatMessage },
  verificationRequired,
  ...props
}) => {
  const classes = useStyles()
  const [step, setStep] = useState(0)
  const [consentOptions, setConsentOptions] = useState([])
  const [policyDocs, setPolicyDocs] = useState([])
  const [standalonePolicyDocs, setStandalonePolicyDocs] = useState(0)
  const [nonStandalonePolicyDocs, setNonStandalonePolicyDocs] = useState(0)
  const [termsAccepted, setTermsAccepted] = useState([])
  const [customerData, setCustomerData] = useState(null)
  const [deviceId, setDeviceId] = useState("")
  const { executeRecaptcha } = useGoogleReCaptcha()
  const {
    state: { loading, tenantId, config, tenantSettings },
    setLoading,
    toggleBackdrop,
    setCurrentUser,
    setError,
    setAuthenticated,
  } = useGlobalContext()
  const { fetchCustomer } = useCustomer()

  const mfaEnabled =
    tenantSettings.mfaEnabled && tenantSettings.mfaEnabledForStaff

  useEffect(() => {
    ;(async () => {
      if (mfaEnabled) {
        const fp = await FingerprintJS.load()
        const result = await fp.get()
        setDeviceId(result.visitorId)
      }
    })()
  }, [])

  const { location } = useCurrentLocation()
  const consentOptionsNeeded =
    consentOptions.length > 0 &&
    config?.should_agree_and_skip_consent_options === false

  const minChars = tenantSettings?.passswordPolicy.minimumNumberOfCharacters

  const goToNextStep = (formData) => {
    if (consentOptionsNeeded) {
      setStep(1)
    } else createCustomer([], formData)
  }

  const authenticateCustomer = async (postData) => {
    toggleBackdrop(true)
    const resp = await Ridecell.login({
      email: postData["email"],
      password: postData["password"],
      ...(mfaEnabled && { deviceId: deviceId }),
    })
    await fetchCustomer()
    setAuthenticated(true)
    toggleBackdrop(false)
    handleNext(resp)
  }

  const checkValid = (isValid) => {
    return !(isValid && standalonePolicyDocs === termsAccepted.length)
  }

  const createCustomer = async (acceptedOptions, formData) => {
    let consentDecisions = []
    let newCustomerData = formData || customerData
    let token
    if (tenantSettings.enableCaptcha)
      token = await executeRecaptcha("createCustomer")
    consentOptions.forEach((option) => {
      let decision = {}
      decision["consent_option"] = option.id
      decision["option_version"] = option.option_version
      decision["user_decision"] =
        !consentOptionsNeeded ||
        (acceptedOptions.findIndex((o) => o === option.id) !== -1
          ? true
          : false)
      consentDecisions.push(decision)
    })
    let policyAgreements = policyDocs.reduce((acc, cur) => {
      let obj = {}
      obj["document_version"] = cur.document_version
      obj["policy_document"] = cur.id
      acc.push(obj)
      return acc
    }, [])
    let postData = {
      ...newCustomerData,
      consent_decisions: consentDecisions,
      policy_document_agreements: policyAgreements,
      ...(token && { captcha_token: token }),
      ...(location && { registration_lat: location.lat }),
      ...(location && { registration_lng: location.lng }),
      ...(mfaEnabled && { device_id: deviceId }),
    }
    setLoading(true)
    try {
      const customer = await Ridecell.createCustomer(postData)
      setCurrentUser(customer)
      authenticateCustomer(postData)
    } catch (e) {
      setError(e)
      if (step === 1) setStep(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      toggleBackdrop(true)
      try {
        let resp = await Ridecell.getConsentOptions({
          page: 1,
          page_size: 25,
          tenant_id: tenantId,
        })
        setConsentOptions(resp.results)
        resp = await Ridecell.getPolicyDocs({
          page: 1,
          page_size: 25,
          tenant_id: tenantId,
        })
        setPolicyDocs(resp.results)
      } catch (e) {
        setError(e)
      }
      toggleBackdrop(false)
    })()
  }, [])

  useEffect(() => {
    const countStandAlonePolicyDocs =
      (policyDocs && policyDocs.filter((doc) => doc.is_standalone)) || []
    const standAlonePolicyDocsLength = countStandAlonePolicyDocs.length
    setStandalonePolicyDocs(standAlonePolicyDocsLength)
    setNonStandalonePolicyDocs(policyDocs.length - standAlonePolicyDocsLength)
  }, [policyDocs])

  const shapeInitialValues = () => {
    return {
      first_name: customerData?.first_name || "",
      last_name: customerData?.last_name || "",
      email: customerData?.email || "",
      password: customerData?.password || "",
      phone_number: customerData?.phone_number || "",
      terms_accepted: customerData?.terms_accepted || false,
      business_profile_activation_code:
        customerData?.business_profile_activation_code || "",
    }
  }

  const onCheckBoxChange = (e, index) => {
    e.target.checked
      ? setTermsAccepted([...termsAccepted, index])
      : setTermsAccepted([...termsAccepted.filter((idx) => idx !== index)])
  }

  return (
    <React.Fragment>
      {step === 0 && (
        <Grid item xs={12} md={12} lg={6}>
          <Card className={classes.card}>
            <FormikFormContainer
              initialValues={shapeInitialValues()}
              onSubmit={async (values) => {
                setCustomerData(values)
                goToNextStep(values)
              }}
              validationSchema={validationSchema(
                formatMessage,
                tenantSettings.passswordPolicy,
                tenantSettings.businessInviteCodeMandatory
              )}
            >
              {({
                isValid,
                values,
                setFieldValue,
                errors,
                setTouched,
                touched,
              }) => (
                <FormikForm>
                  <Box display="flex" sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label={formatMessage(messages.firstName)}
                      name="first_name"
                      required
                      value={values.first_name}
                    />
                  </Box>
                  <Box display="flex" sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label={formatMessage(messages.lastName)}
                      name="last_name"
                      required
                      value={values.last_name}
                    />
                  </Box>
                  <Box display="flex" sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      label={formatMessage(messages.email)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="email"
                      variant="standard"
                      required
                      value={values.email}
                    />
                  </Box>
                  <Box
                    display="flex"
                    sx={{ mb: "16px" }}
                    flexDirection="column"
                  >
                    <PhoneNumberInput
                      id="phone-input"
                      helperText={
                        errors.phone_number && touched.phone_number
                          ? errors.phone_number
                          : ""
                      }
                      error={errors.phone_number && touched.phone_number}
                      label={formatMessage(messages.phoneNumber)}
                      name="phone_number"
                      onChange={(phone) => {
                        setTouched({ ...touched, phone_number: true })
                        setFieldValue("phone_number", phone)
                      }}
                      required
                      type="text"
                      value={values.phone_number || ""}
                    />
                  </Box>
                  <Box display="flex" sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      label={formatMessage(messages.password)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="password"
                      type="password"
                      helperText={formatMessage(
                        messages.mustBeAtMiniumCharacters,
                        { minChars }
                      )}
                      variant="standard"
                      required
                      value={values.password}
                    />
                  </Box>
                  <Box display="flex" sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      label={formatMessage(messages.businessInviteCode)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="business_profile_activation_code"
                      required={tenantSettings.businessInviteCodeMandatory}
                      variant="standard"
                      value={values.business_profile_activation_code}
                    />
                  </Box>
                  {policyDocs.length > 0 &&
                    standalonePolicyDocs > 0 &&
                    policyDocs.map((doc, index) => {
                      const messageKey = doc.policy_checkbox_text_key
                      return doc.is_standalone ? (
                        <Box display="flex">
                          <Checkbox
                            size="medium"
                            checked={termsAccepted.includes(index)}
                            onChange={(e) => {
                              onCheckBoxChange(e, index)
                            }}
                          />
                          <Typography
                            style={{
                              paddingTop: "9px",
                            }}
                          >
                            {formatMessage(policyKeyMessages(messageKey))}
                            <Link
                              underline="always"
                              target="_blank"
                              href={doc.document_url}
                            >
                              {doc.title}
                            </Link>
                          </Typography>
                        </Box>
                      ) : null
                    })}
                  {policyDocs.length > 0 && nonStandalonePolicyDocs > 0 && (
                    <Box display="flex" sx={{ mb: "16px" }}>
                      <Field name="terms_accepted">
                        {({ form }) => (
                          <Box display="flex" flexDirection="row">
                            <Checkbox
                              size="medium"
                              name={`terms_accepted`}
                              checked={values.terms_accepted}
                              onChange={(e) => {
                                form.setFieldValue(
                                  "terms_accepted",
                                  e.target.checked
                                )
                              }}
                            />

                            <Box
                              display="flex"
                              justifyContent="center"
                              flexDirection="column"
                              alignContent="center"
                            >
                              <Typography className={classes.title}>
                                {formatMessage(messages.iHaveReadAndAcceptThe)}
                                {policyDocs.map((doc, index) => {
                                  return !doc.is_standalone ? (
                                    <Link
                                      underline="always"
                                      target="_blank"
                                      href={doc.document_url}
                                    >
                                      {doc.title}
                                      {policyDocs.length - 1 !== index && ","}
                                    </Link>
                                  ) : null
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Field>
                    </Box>
                  )}
                  <LoadingButton
                    disabled={checkValid(isValid)}
                    text={formatMessage(messages.next)}
                    variant="contained"
                    loading={loading}
                    fullWidth
                    props={{
                      fullWidth: true,
                      type: "submit",
                      endIcon: <ArrowForwardIcon />,
                      size: "large",
                    }}
                  />
                </FormikForm>
              )}
            </FormikFormContainer>
          </Card>
        </Grid>
      )}
      {step === 1 && (
        <ConsentOptions options={consentOptions} handleNext={createCustomer} />
      )}
    </React.Fragment>
  )
}

export default injectIntl(CreateCustomer)
